/**
 * commitVerifiedSession — the single, shared "trust this token → authenticate"
 * gate for every credential-completion path (login success, OTP verify, TOTP
 * verify).
 *
 * SECURITY BOUNDARY. A 2xx auth response is NOT proof of a valid session: the
 * body's `accessToken` could be garbage ("undecodable"), already expired, or
 * carry the WRONG audience (a mobile token replayed at the admin surface). The
 * previous per-path code decoded the token but SUBSTITUTED a fresh expiry
 * (`decodeJwtExp(token) ?? now + 900`) when decode failed, so a garbage /
 * expired / wrong-aud token was stored and yielded `isAuthenticated: true` —
 * enough to pass client route guards. This function closes that hole for all
 * paths at once.
 *
 * It VALIDATES before mutating any store:
 *   1. the token decodes to a non-null claims object;
 *   2. `exp` is present AND strictly in the future;
 *   3. the identity subject (`sub`) is present;
 *   4. `aud` EXACTLY equals the audience this app expects.
 * On any failure it throws an ApiError-shaped `AUTH_UNEXPECTED_RESPONSE` and
 * touches NOTHING (no token store, no refresh store, no timer) — the caller
 * stays unauthenticated and fails closed.
 *
 * Only after validation passes does it store the access token, seed the email
 * hint, kick the proactive-refresh timer, hydrate effective permissions
 * (fail-closed to `[]`), and return the AuthSnapshot.
 */
import { accessTokenStore } from "../tokens/accessTokenStore.js";
import { loggedOutMarker } from "../tokens/loggedOutMarker.js";
import { noEmployeeMarker } from "../tokens/noEmployeeMarker.js";
import { sessionTenantMarker } from "../tokens/sessionTenantMarker.js";
import { userEmailHintStore } from "../tokens/userEmailHint.js";
import { decodeJwtClaims, decodeJwtExp } from "../tokens/jwt.js";
import { scheduleProactiveRefresh } from "../refresh/proactiveTimer.js";
import { hasHrReads } from "../refresh/authNetworkConfig.js";
import { fetchEffectivePermissions } from "../bootstrap/effectivePermissions.js";
import type { AuthSnapshot, Audience } from "../types.js";

export interface CommitVerifiedSessionArgs {
  /**
   * The access token from the auth response body. A body `refreshToken` is
   * deliberately not taken: the response's HttpOnly cookie carries it, and a
   * client-side copy can fall behind that cookie (see refreshTokenStore).
   */
  accessToken: string;
  /** The audience THIS app runs as. The token's `aud` claim must match exactly. */
  expectedAudience: Audience;
  apiBaseUrl: string;
  /**
   * Optional identity overrides from the response body (MSW back-compat — the
   * real backend response is tokens-only, so these are usually absent and
   * identity is derived from the JWT claims).
   */
  bodyUser?:
    | {
        id?: string;
        email?: string | null;
        displayName?: string;
        roles?: string[];
        username?: string | null;
      }
    | undefined;
  /**
   * What the user typed at login (email or username). Only used as the email
   * fallback when it contains `@` — a typed username is never filed as an
   * email. OTP/TOTP paths may omit it.
   */
  emailHint?: string | undefined;
  /**
   * The response's `mustChangePassword`. True → the token opens only the
   * password change, refresh and logout, so the permissions fetch is skipped
   * (it would 403) and the snapshot is marked restricted.
   */
  mustChangePassword?: boolean | undefined;
  /**
   * Whether to hydrate effective permissions into the snapshot. Tenant paths
   * (login/otp/totp) pass `true` (default). Platform staff use role-based
   * gating, not a permission catalog, so platform login passes `false` and the
   * snapshot keeps `effectivePermissions: undefined`. Only the hydrate step
   * differs — validation is identical across every path.
   * @default true
   */
  hydratePermissions?: boolean;
  /**
   * `x-correlation-id` from the auth response, threaded onto the thrown
   * validation-failure error so support can trace a rejected 2xx (matches the
   * pre-refactor per-path behavior that attached it).
   */
  correlationId?: string | null | undefined;
}

/** ApiError-shaped rejection — fails closed, stores nothing. */
function invalidResponse(reason: string, correlationId: string | null | undefined): Error {
  return Object.assign(new Error("Authentication response failed validation"), {
    status: 200,
    errorCode: "AUTH_UNEXPECTED_RESPONSE",
    isApiError: true,
    detail: reason,
    correlationId: correlationId ?? null,
  });
}

export async function commitVerifiedSession({
  accessToken,
  expectedAudience,
  apiBaseUrl,
  bodyUser,
  emailHint,
  hydratePermissions = true,
  correlationId,
  mustChangePassword = false,
}: CommitVerifiedSessionArgs): Promise<AuthSnapshot> {
  // ---- VALIDATE (before any store mutation) ---------------------------------
  // Guard the type at runtime too — a malformed body ({}) can hand us a
  // non-string here despite the compile-time `string`.
  if (typeof accessToken !== "string" || accessToken.length === 0) {
    throw invalidResponse("token missing", correlationId);
  }
  const claims = decodeJwtClaims(accessToken);
  if (claims === null) {
    throw invalidResponse("token undecodable", correlationId);
  }
  // `exp` MUST be present and in the future. Never substitute a synthetic
  // expiry — a token whose expiry we can't read is not a session.
  const exp = decodeJwtExp(accessToken);
  const nowSec = Math.floor(Date.now() / 1000);
  if (exp === null || exp <= nowSec) {
    throw invalidResponse("token expiry missing or in the past", correlationId);
  }
  if (typeof claims.sub !== "string" || claims.sub.length === 0) {
    throw invalidResponse("token subject (sub) missing", correlationId);
  }
  // Audience is the cross-surface boundary: a mobile/platform token must never
  // authenticate the admin app (and vice-versa). Exact match, no fallback.
  if (claims.aud !== expectedAudience) {
    throw invalidResponse(`token audience mismatch (expected ${expectedAudience})`, correlationId);
  }

  // ---- COMMIT (validation passed) -------------------------------------------
  accessTokenStore.set(accessToken, exp, nowSec);
  // A fresh verified session supersedes any prior explicit logout — let the
  // next bootstrap attempt its silent refresh again.
  loggedOutMarker.clear();
  // Whether this login has an employee record is asked afresh after a sign-in.
  noEmployeeMarker.clear();
  // Record which tenant this host's session belongs to. The refresh cookie is
  // shared across tenant subdomains, so this host-only marker is the only thing
  // that can later tell "reload of my own tenant" from "someone else's tenant
  // riding my cookie". Platform carries no tenant, so it records nothing.
  if (claims.aud !== "platform") {
    sessionTenantMarker.set(
      (claims as typeof claims & { tid?: string | null }).tid ?? claims.tenant_id,
    );
  }

  // `claims.sub` is the canonical user id. The email comes from the response
  // body (null for a username-only employee), else from what was typed — but
  // only when that was an address: a typed username is not an email.
  const claimsLoose = claims as typeof claims & { tid?: string };
  const typedEmail = emailHint !== undefined && emailHint.includes("@") ? emailHint : undefined;
  const userEmail = bodyUser?.email ?? typedEmail ?? "";
  const username = bodyUser?.username ?? null;
  // The display hint (restored on reload) names the person: email, else username.
  const hint = userEmail !== "" ? userEmail : (username ?? "");
  if (hint !== "") {
    userEmailHintStore.set(hint);
  }
  const userId = bodyUser?.id ?? claims.sub;
  const userDisplayName =
    bodyUser?.displayName ?? (userEmail !== "" ? (userEmail.split("@")[0] ?? userEmail) : hint);
  const tenantId = claimsLoose.tid ?? claims.tenant_id ?? null;

  scheduleProactiveRefresh();

  // Hydrate effectivePermissions before committing the snapshot. Fail-closed
  // (returns []) on any non-2xx / network error, so this never throws. Platform
  // staff use role-based gating (no permission catalog) → skip + leave
  // `effectivePermissions: undefined`.
  let effectivePermissions: ReadonlySet<string> | undefined;
  // A restricted token can't read permissions (403); the snapshot stays
  // unhydrated until the password change finishes the session. An accounting
  // bearer is refused there too: its permissions come from /me/permissions.
  if (hydratePermissions && !mustChangePassword && hasHrReads(expectedAudience)) {
    const perms = await fetchEffectivePermissions({ apiBaseUrl, accessToken });
    effectivePermissions = new Set(perms);
  }

  return {
    isAuthenticated: true,
    user: {
      id: userId,
      email: userEmail,
      displayName: userDisplayName,
      username,
    },
    // `claims.sub`, never `user.id` — the latter is replaced by the employee id
    // once /employees/me hydrates. See AuthSnapshot.userId.
    userId: claims.sub,
    roles: claims.roles ?? bodyUser?.roles ?? [],
    tenantId,
    // Safe to use expectedAudience — validated equal to claims.aud above.
    aud: expectedAudience,
    accessToken,
    effectivePermissions,
    mustChangePassword,
  };
}
